import type { Draft } from 'immer';
import type { Vec2 } from '@indusketch/geometry';
import { pointsConnected } from '../interior.js';
import type { ProjectDocument } from '../types.js';

/** A drawn wall as it was before it moved. */
export interface MovedWall {
  readonly id: string;
  readonly start: Vec2;
  readonly end: Vec2;
  readonly thickness: number;
  readonly level: number;
}

/** An end this far beside a wall's face (or past its end) still meets it. */
const T_JOINT_TOLERANCE = 0.05;

/**
 * The end `p` of a wall coming from `from`, if it met wall `m` (before `m` moved by `delta`)
 * in a T: moved along its own wall by as much as `m` moved across, so it stays against `m`.
 */
export function followTJoint(from: Vec2, p: Vec2, m: MovedWall, delta: Vec2): Vec2 {
  const len = Math.hypot(m.end.x - m.start.x, m.end.y - m.start.y);
  const own = Math.hypot(p.x - from.x, p.y - from.y);
  if (len < 1e-6 || own < 1e-6) return p;
  const u = { x: (m.end.x - m.start.x) / len, y: (m.end.y - m.start.y) / len };
  const n = { x: -u.y, y: u.x };
  const rel = { x: p.x - m.start.x, y: p.y - m.start.y };
  const across = rel.x * n.x + rel.y * n.y;
  const along = rel.x * u.x + rel.y * u.y;
  // Anywhere in the moved wall: on a face, inside it, or at its ends (an L-corner whose ends
  // do not sit exactly on each other).
  const reach = m.thickness / 2 + T_JOINT_TOLERANCE;
  if (Math.abs(across) > reach) return p;
  if (along < -reach || along > len + reach) return p;
  const d = { x: (p.x - from.x) / own, y: (p.y - from.y) / own };
  const c = d.x * n.x + d.y * n.y;
  // Running nearly along the moved wall: no T.
  if (Math.abs(c) < 0.2) return p;
  const k = (delta.x * n.x + delta.y * n.y) / c;
  return { x: p.x + d.x * k, y: p.y + d.y * k };
}

/**
 * Drawn walls meeting one of the `shifted` walls in a T or a corner (they moved by `delta` without
 * turning) are drawn on or back along themselves, so they stay against it: a corridor wall
 * pushed out lengthens the stubs on it. Locked walls, shells and the moved walls stay.
 */
export function followTJoints(
  draft: Draft<ProjectDocument>,
  shifted: readonly MovedWall[],
  delta: Vec2,
): void {
  if (shifted.length === 0 || (delta.x === 0 && delta.y === 0)) return;
  const moved = new Set(shifted.map((s) => s.id));
  // Ends that went along as exact corners already sit on a moved wall's new end.
  const newEnds = shifted.flatMap((s) => {
    const w = draft.walls[s.id];
    return w ? [w.start, w.end] : [];
  });
  const along = (p: Vec2) => newEnds.some((q) => pointsConnected(p, q));
  for (const w of Object.values(draft.walls)) {
    if (moved.has(w.id) || w.ownerId !== null || w.locked) continue;
    for (const s of shifted) {
      if (s.level !== w.level) continue;
      if (!along(w.end)) w.end = followTJoint(w.start, w.end, s, delta);
      if (!along(w.start)) w.start = followTJoint(w.end, w.start, s, delta);
    }
  }
}
